import { useEffect, useMemo, useState } from 'react'

import { evaluatePaths } from './utils/evaluatePaths'
import {
  isSimulationSandboxLoaded,
  loadSimulationSandbox,
  setupExpressionEvaluator
} from './utils/evaluateExpression'
import type {
  AutomationAnalysis,
  DependencyResolutionRunOutcome,
  ResolutionContext,
  SimulationEvaluation
} from './simulation.types'

/** Runs each route's conditions and reports where the automation ends up. */
export const useEvaluation = ({
  analysis,
  isEnabled,
  resolution,
  workflowInput
}: {
  analysis: AutomationAnalysis | undefined
  isEnabled: boolean
  resolution: DependencyResolutionRunOutcome | undefined
  workflowInput: ResolutionContext['workflowInput']
}) => {
  const sandbox = useSimulationSandbox(isEnabled)

  const taskOutputs =
    resolution && 'taskOutputs' in resolution
      ? resolution.taskOutputs
      : undefined

  const evaluation = useMemo<EvaluationRun | undefined>(() => {
    if (!isEnabled || !analysis || !taskOutputs) return undefined

    if (sandbox === 'loading') return undefined

    const startedAt = performance.now()
    const value = runConditions({
      analysis,
      context: { workflowInput, taskOutputs }
    })

    return { value, durationMs: Math.round(performance.now() - startedAt) }
  }, [analysis, isEnabled, sandbox, taskOutputs, workflowInput])

  return {
    evaluation: evaluation?.value.result,
    // Reported rather than logged here, so one event can carry every stage's
    // timing instead of each stage sending its own.
    evaluationDurationMs: evaluation?.durationMs,
    // Kept alongside the answer so the rules inspector can show what each
    // switch said against the condition it came from.
    expressionResults: evaluation?.value.expressionResults ?? {},
    isEvaluating: isEnabled && Boolean(taskOutputs) && evaluation === undefined
  }
}

/** An answer, and how long the conditions took to run. */
type EvaluationRun = {
  value: SimulationEvaluation
  durationMs: number
}

/**
 * One sandbox for the whole run, closed however it ends. A route that blows up
 * part way through would otherwise leave WebAssembly memory behind, which
 * nothing else is going to tidy up.
 */
const runConditions = ({
  analysis,
  context
}: {
  analysis: AutomationAnalysis
  context: ResolutionContext
}): SimulationEvaluation => {
  const evaluator = setupExpressionEvaluator()

  // No sandbox? No evaluation for you.
  if (!evaluator) {
    return {
      result: { outcome: 'INDETERMINATE', reason: 'EVALUATION_FAILED' },
      expressionResults: {}
    }
  }

  try {
    return evaluatePaths({ analysis, context, evaluate: evaluator.evaluate })
  } finally {
    evaluator.dispose()
  }
}

type SandboxState = 'loading' | 'ready' | 'unavailable'

/**
 * Fetches and compiles the engine the conditions run in. This is the only part
 * that has to be waited for, so it's done once here and everything downstream
 * of it is synchronous.
 */
const useSimulationSandbox = (isEnabled: boolean): SandboxState => {
  const [state, setState] = useState<SandboxState>(() =>
    isSimulationSandboxLoaded() ? 'ready' : 'loading'
  )

  useEffect(() => {
    if (!isEnabled || state !== 'loading') return

    let isMounted = true

    void loadSimulationSandbox().then(loaded => {
      if (isMounted) setState(loaded ? 'ready' : 'unavailable')
    })

    return () => {
      isMounted = false
    }
  }, [isEnabled, state])

  return state
}
